import { useValue } from "@legendapp/state/react";
import { Icon } from "@repo/ui/components/icon";
import { Button } from "@repo/ui/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@repo/ui/ui/empty";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import {
  isDatasourceLimitReached,
  isFivetranLimitReached,
  subscriptionLoaded$,
  type UpgradeReason,
} from "~/stores/subscription";

const COPY: Record<
  Exclude<UpgradeReason, "general">,
  { title: string; description: string }
> = {
  datasources: {
    title: "Datasource limit reached",
    description:
      "Your current plan doesn't allow another datasource. Upgrade to connect more.",
  },
  fivetran: {
    title: "Connectors need a paid plan",
    description:
      "Connectors aren't included on your current plan. Upgrade to sync from third-party services.",
  },
};

/**
 * Wraps a creation flow (new datasource / new connection) and blocks it with
 * an upgrade prompt when the org is at its plan limit. Server-side enforcement
 * is the real guarantee — this is the pre-emptive UX so the user isn't sent
 * through a whole form only to be rejected. Renders children optimistically
 * until the subscription has loaded to avoid a false block on cold loads.
 */
export const PlanGate = ({
  reason,
  children,
}: {
  reason: Exclude<UpgradeReason, "general">;
  children: ReactNode;
}) => {
  const loaded = useValue(subscriptionLoaded$);
  const reached = useValue(() =>
    reason === "fivetran"
      ? isFivetranLimitReached()
      : isDatasourceLimitReached(),
  );

  if (!loaded || !reached) return <>{children}</>;

  const copy = COPY[reason];
  return (
    <Empty className="min-h-96">
      <EmptyHeader>
        <EmptyTitle>{copy.title}</EmptyTitle>
        <EmptyDescription>{copy.description}</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button render={<Link to="/settings/plans" />}>
          <Icon name="card" />
          Upgrade plan
        </Button>
      </EmptyContent>
    </Empty>
  );
};
